(() => {
class RealityCheckBundleBuilder {
  constructor(root) {
    this.root = root;
    this.config = JSON.parse(root.querySelector('[data-bundle-config]').textContent);
    this.selection = new Map();
    this.currency = root.dataset.currency || 'EUR';
    this.discountPercent = Math.min(100, Math.max(0, Number(this.config.discountPercent) || 0));
    this.requiredProductCount = Math.max(
      this.config.products.length,
      Number(this.config.requiredProductCount) || 0,
    );
    this.addButton = root.querySelector('[data-add-bundle]');
    this.galleryMode = 'composite';
    this.galleryIndex = 0;
    this.gallerySlides = [];
    this.hasProductGallery = false;
    this.preloadedImages = new Map();
    this.announcedGiftMilestones = new Set();

    this.config.products.forEach((product) => {
      const initial = product.variants.find((variant) => variant.available) || product.variants[0];
      if (initial) this.selection.set(product.key, initial);
    });

    this.bind();
    this.render();
    this.preloadVariantImages();
  }

  preloadVariantImages() {
    const selectedUrls = new Set([...this.selection.values()].map((variant) => variant.image).filter(Boolean));
    const urls = [...new Set(this.config.products.flatMap((product) => product.variants.map((variant) => variant.image)).filter(Boolean))]
      .filter((url) => !selectedUrls.has(url));
    let cursor = 0;

    const loadNext = () => {
      if (cursor >= urls.length) return;
      const url = urls[cursor];
      cursor += 1;

      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'low';
      this.preloadedImages.set(url, image);
      const done = () => {
        image.onload = null;
        image.onerror = null;
        loadNext();
      };
      image.onload = done;
      image.onerror = done;
      image.src = url;
    };

    const start = () => {
      const concurrency = Math.min(4, urls.length);
      for (let index = 0; index < concurrency; index += 1) loadNext();
    };

    // Keep variant warming away from the LCP window and respect reduced-data users.
    if (navigator.connection?.saveData) return;
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 1800 });
    } else {
      window.setTimeout(start, 1200);
    }
  }

  preloadGalleryImages(galleryItems) {
    const urls = [...new Set(galleryItems.map((item) => item.src).filter(Boolean))]
      .filter((url) => !this.preloadedImages.has(url));
    let cursor = 0;

    const loadNext = () => {
      if (cursor >= urls.length) return;
      const url = urls[cursor];
      cursor += 1;
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'low';
      this.preloadedImages.set(url, image);
      image.onload = loadNext;
      image.onerror = loadNext;
      image.src = url;
    };

    const start = () => {
      const concurrency = Math.min(2, urls.length);
      for (let index = 0; index < concurrency; index += 1) loadNext();
    };

    if (navigator.connection?.saveData) return;
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 2200 });
    } else {
      window.setTimeout(start, 1600);
    }
  }

  rebuildGallery() {
    const slides = [{ mode: 'composite', source: 'composite', src: '', alt: 'Bundle-Ansicht' }];
    const seen = new Set();

    this.root.querySelectorAll('[data-gallery-thumb][data-gallery-mode="image"]').forEach((thumbnail) => {
      const src = thumbnail.dataset.galleryImage;
      if (!src || seen.has(src)) return;
      seen.add(src);
      slides.push({ mode: 'image', source: 'fixed', src, alt: thumbnail.dataset.galleryAlt || '' });
    });

    const productGalleryItems = [];
    this.config.products.forEach((product) => {
      if (!product.includeGallery) return;
      const variant = this.selection.get(product.key);
      (variant?.gallery || []).forEach((item) => {
        if (!item?.src || seen.has(item.src)) return;
        seen.add(item.src);
        const slide = {
          mode: 'image',
          source: 'product',
          src: item.src,
          alt: item.alt || `${product.title} – ${variant.title}`,
        };
        slides.push(slide);
        productGalleryItems.push(slide);
      });
    });

    this.gallerySlides = slides;
    this.galleryIndex = 0;
    this.hasProductGallery = productGalleryItems.length > 0;
    this.showGallerySlide(0);
    this.preloadGalleryImages(productGalleryItems);
  }

  showGallerySlide(index) {
    if (!this.gallerySlides.length) return;
    const count = this.gallerySlides.length;
    this.galleryIndex = ((index % count) + count) % count;
    const slide = this.gallerySlides[this.galleryIndex];
    this.galleryMode = slide.mode;

    const override = this.root.querySelector('[data-gallery-override]');
    const layers = this.root.querySelectorAll('[data-layer-key]');
    if (slide.mode === 'composite') {
      if (override) {
        override.hidden = true;
        override.classList.remove('is-product-gallery');
      }
      layers.forEach((layer) => { layer.hidden = false; });
    } else {
      if (override) {
        override.src = slide.src;
        override.alt = slide.alt || '';
        override.hidden = false;
        override.classList.toggle('is-product-gallery', slide.source === 'product');
      }
      layers.forEach((layer) => { layer.hidden = true; });
    }

    const thumbnails = [...this.root.querySelectorAll('[data-gallery-thumb]')];
    let activeThumbnailIndex = -1;
    thumbnails.forEach((button, thumbnailIndex) => {
      const selected = slide.mode === 'composite'
        ? button.dataset.galleryMode === 'composite'
        : slide.source === 'fixed' && button.dataset.galleryImage === slide.src;
      if (selected) activeThumbnailIndex = thumbnailIndex;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    this.root.querySelectorAll('[data-gallery-dot]').forEach((dot, dotIndex) => {
      dot.classList.toggle('is-selected', dotIndex === activeThumbnailIndex);
    });

    this.root.querySelectorAll('[data-gallery-previous], [data-gallery-next]').forEach((arrow) => {
      arrow.hidden = !this.hasProductGallery;
    });
  }

  bind() {
    this.root.addEventListener('click', (event) => {
      const option = event.target.closest('[data-variant-option]');
      if (!option || !this.root.contains(option)) return;

      const product = this.config.products.find((item) => item.key === option.dataset.productKey);
      const variant = product?.variants.find((item) => String(item.id) === option.dataset.variantId);
      if (!variant || !variant.available) return;

      this.selection.set(product.key, variant);
      this.render();
    });

    this.root.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('[data-gallery-thumb]');
      if (!thumbnail || !this.root.contains(thumbnail)) return;
      const slideIndex = this.gallerySlides.findIndex((slide) => thumbnail.dataset.galleryMode === 'composite'
        ? slide.mode === 'composite'
        : slide.source === 'fixed' && slide.src === thumbnail.dataset.galleryImage);
      if (slideIndex >= 0) this.showGallerySlide(slideIndex);
    });

    this.root.addEventListener('click', (event) => {
      const previous = event.target.closest('[data-gallery-previous]');
      const next = event.target.closest('[data-gallery-next]');
      if ((!previous && !next) || !this.root.contains(event.target)) return;
      this.showGallerySlide(this.galleryIndex + (previous ? -1 : 1));
    });

    this.addButton?.addEventListener('click', () => this.addToCart());
  }

  money(cents) {
    const hasFraction = (cents || 0) % 100 !== 0;
    return new Intl.NumberFormat(this.root.dataset.locale || 'de-DE', {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0,
    }).format((cents || 0) / 100).replace(/\s+(?=€)/, '');
  }

  moneyWithoutCents(cents) {
    return this.money(Math.floor(Math.max(0, cents || 0) / 100) * 100);
  }

  announceGiftUnlock(type, label) {
    if (!this.config.confettiEnabled || this.announcedGiftMilestones.has(type)) return;
    this.announcedGiftMilestones.add(type);
    const detail = { type, label: label || 'Gratisgeschenk' };
    window.rcGiftUnlockQueue = window.rcGiftUnlockQueue || [];
    window.rcGiftUnlockQueue.push(detail);
    document.dispatchEvent(new CustomEvent('rc:gift-unlocked', { detail }));
  }

  render() {
    let subtotal = 0;
    let originalTotal = 0;
    let allAvailable = true;

    this.config.products.forEach((product) => {
      const variant = this.selection.get(product.key);
      if (!variant) return;

      allAvailable = allAvailable && variant.available;
      originalTotal += Math.max(variant.compareAt || 0, variant.price);
      if (product.priceTreatment !== 'included') {
        subtotal += variant.price;
      }

      this.root.querySelectorAll(`[data-variant-option][data-product-key="${CSS.escape(product.key)}"]`).forEach((button) => {
        const selected = button.dataset.variantId === String(variant.id);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      const layer = this.root.querySelector(`[data-layer-key="${CSS.escape(product.key)}"]`);
      if (layer && variant.image && layer.src !== new URL(variant.image, window.location.href).href) {
        layer.src = variant.image;
        layer.alt = `${product.title} – ${variant.title}`;
      }

      const card = this.root.querySelector(`[data-product-card="${CSS.escape(product.key)}"]`);
      if (card) {
        const image = card.querySelector('[data-selected-image]');
        const title = card.querySelector('[data-selected-title]');
        const price = card.querySelector('[data-selected-price]');
        if (image && (variant.thumbnail || variant.image)) image.src = variant.thumbnail || variant.image;
        if (title) title.textContent = variant.title;
        if (price) price.textContent = product.priceTreatment === 'included' ? product.includedLabel : this.money(variant.price);
      }
    });

    const originalNode = this.root.querySelector('[data-original-total]');
    const savingsNode = this.root.querySelector('[data-savings]');
    const totalNode = this.root.querySelector('[data-bundle-total]');
    const bundleDiscount = Math.round(subtotal * (this.discountPercent / 100));
    const total = Math.max(0, subtotal - bundleDiscount);
    const savings = Math.max(0, originalTotal - total);
    this.bundleTotal = total;

    if (originalNode) originalNode.textContent = this.money(originalTotal);
    if (savingsNode) savingsNode.textContent = this.money(savings);
    if (totalNode) totalNode.textContent = this.money(total);
    const heroSaving = this.root.querySelector('[data-hero-saving]');
    const heroSavingPercent = this.root.querySelector('[data-hero-saving-percent]');
    const heroSavingAmount = this.root.querySelector('[data-hero-saving-amount]');
    if (heroSaving) heroSaving.hidden = bundleDiscount <= 0;
    if (heroSavingPercent) heroSavingPercent.textContent = `−${this.discountPercent}%`;
    if (heroSavingAmount) heroSavingAmount.textContent = this.money(bundleDiscount);
    if (this.addButton) {
      const complete = this.config.products.length > 0
        && this.selection.size === this.config.products.length
        && this.selection.size >= this.requiredProductCount;
      this.addButton.disabled = !allAvailable || !complete;
    }

    const heroPrice = this.root.querySelector('[data-hero-price]');
    const heroCompare = this.root.querySelector('[data-hero-compare]');
    if (heroPrice) heroPrice.textContent = this.money(total);
    if (heroCompare) {
      heroCompare.textContent = originalTotal > total ? this.money(originalTotal) : '';
      heroCompare.hidden = originalTotal <= total;
    }

    this.rebuildGallery();

    const shippingThreshold = this.config.shippingThreshold || 0;
    const giftThreshold = Math.max(shippingThreshold, this.config.giftThreshold || 0);
    const cartTotal = this.config.cartTotal || 0;
    const projectedTotal = cartTotal + total;
    const progress = giftThreshold > 0 ? Math.min(100, Math.max(0, (projectedTotal / giftThreshold) * 100)) : 100;
    const progressBar = this.root.querySelector('[data-rewards-progress]');
    const rewardsCopy = this.root.querySelector('[data-rewards-copy]');
    const shippingMarker = this.root.querySelector('[data-reward-shipping-marker]');
    const giftMarker = this.root.querySelector('[data-reward-gift-marker]');
    this.qualifiesForThresholdGift = giftThreshold > 0 && projectedTotal >= giftThreshold;
    if (progressBar) {
      progressBar.style.width = `${progress}%`;
      progressBar.parentElement?.setAttribute('aria-valuenow', String(Math.round(progress)));
    }
    if (shippingMarker) {
      const shippingPosition = giftThreshold > 0 ? Math.min(100, Math.max(0, (shippingThreshold / giftThreshold) * 100)) : 0;
      shippingMarker.style.left = `${shippingPosition}%`;
      shippingMarker.classList.toggle('is-reached', shippingThreshold > 0 && projectedTotal >= shippingThreshold);
    }
    giftMarker?.classList.toggle('is-reached', this.qualifiesForThresholdGift);
    if (rewardsCopy) {
      if (shippingThreshold > 0 && projectedTotal < shippingThreshold) {
        rewardsCopy.textContent = (this.config.rewardBeforeShippingText || 'Noch [amount] bis zum kostenlosen Versand.')
          .replace('[amount]', this.money(shippingThreshold - projectedTotal));
      } else if (giftThreshold > 0 && projectedTotal < giftThreshold) {
        rewardsCopy.textContent = (this.config.rewardBeforeGiftText || 'Noch [amount] bis zum Gratisprodukt.')
          .replace('[amount]', this.moneyWithoutCents(giftThreshold - projectedTotal));
      } else {
        rewardsCopy.textContent = this.config.rewardReachedText || 'Kostenloser Versand und GRATIS Reality Check Notizbuch inkludiert.';
      }
    }
  }

  async addToCart() {
    if (!this.addButton || this.addButton.disabled) return;

    const originalText = this.addButton.textContent;
    const groupId = `bundle-${Date.now()}`;
    const includedGift = this.config.includedGift;
    const thresholdGift = this.config.thresholdGift;
    try {
      window.sessionStorage.removeItem('rcGiftDeclined:ebook');
      window.sessionStorage.removeItem('rcGiftDeclined:notebook');
    } catch (error) {
      // Storage may be unavailable in privacy modes; cart behavior still works.
    }
    // Shopify displays multi-add items in reverse request order. Add gifts first
    // so they appear below the paid bundle products in cart and cart drawer.
    const items = [];
    if (includedGift?.autoAdd && includedGift.available && !includedGift.alreadyInCart) {
      items.push({
        id: includedGift.id,
        quantity: 1,
        properties: {
          _bundle_group: groupId,
          _bundle_template: 'bundle',
          _rc_bundle_gift: 'ebook',
          _gift_limit: '1',
        },
      });
    }
    if (thresholdGift?.autoAdd && this.qualifiesForThresholdGift && thresholdGift.available && !thresholdGift.alreadyInCart) {
      items.push({
        id: thresholdGift.id,
        quantity: 1,
        properties: {
          _bundle_group: groupId,
          _bundle_template: 'bundle',
          _rc_bundle_gift: 'notebook',
          _threshold_gift: 'true',
          _threshold_amount: String(this.config.giftThreshold || 0),
          _gift_limit: '1',
        },
      });
    }
    items.push(...this.config.products.map((product) => ({
      id: this.selection.get(product.key).id,
      quantity: 1,
      properties: {
        _bundle_group: groupId,
        _bundle_template: 'bundle',
        _bundle_role: product.role,
        _bundle_price_treatment: product.priceTreatment,
        _bundle_discount_percent: String(this.discountPercent),
        _bundle_discount_code: String(this.config.discountCode || '').trim(),
        _included_gift_variant: includedGift?.id ? String(includedGift.id) : '',
        _included_gift_auto_add: includedGift?.autoAdd ? 'true' : 'false',
        _threshold_gift_variant: thresholdGift?.id ? String(thresholdGift.id) : '',
        _threshold_gift_auto_add: thresholdGift?.autoAdd ? 'true' : 'false',
        _threshold_amount: String(this.config.giftThreshold || 0),
        _gift_confetti_enabled: this.config.confettiEnabled ? 'true' : 'false',
      },
    })));

    this.addButton.disabled = true;
    this.addButton.textContent = this.root.dataset.addingLabel || 'Wird hinzugefügt …';

    if (this.root.dataset.demo === 'true') {
      this.root.dispatchEvent(new CustomEvent('bundle:added', { bubbles: true, detail: { items, groupId } }));
      this.addButton.textContent = this.root.dataset.addedLabel || 'Bundle ist im Warenkorb';
      setTimeout(() => {
        this.addButton.textContent = originalText;
        this.addButton.disabled = false;
      }, 1800);
      return;
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error((await response.json()).description || 'Cart request failed');

      this.root.dispatchEvent(new CustomEvent('bundle:added', { bubbles: true, detail: { items, groupId } }));
      if (this.config.cartRedirect) {
        const shopRoot = window.Shopify?.routes?.root || '/';
        const cartUrl = `${shopRoot}cart`;
        const discountCode = String(this.config.discountCode || '').trim();
        if (discountCode) {
          const cartResponse = await fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } });
          if (!cartResponse.ok) throw new Error('Cart request failed');
          const cart = await cartResponse.json();
          const discountCodes = new Set([discountCode]);

          cart.items.forEach((item) => {
            const bundleCode = String(item.properties?._bundle_discount_code || '').trim();
            if (bundleCode) discountCodes.add(bundleCode);

            (item.line_level_discount_allocations || []).forEach((allocation) => {
              const application = allocation.discount_application || {};
              const title = String(application.title || '').trim();
              if (application.type === 'discount_code' && title && !/^bundle-journal-/i.test(title)) {
                discountCodes.add(title);
              }
            });
          });

          const discountResponse = await fetch(`${shopRoot}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: Array.from(discountCodes).join(',') }),
          });
          if (!discountResponse.ok) {
            throw new Error((await discountResponse.json()).description || 'Discount request failed');
          }
        }
        window.location.href = cartUrl;
      } else {
        this.addButton.textContent = this.root.dataset.addedLabel || 'Bundle ist im Warenkorb';
        setTimeout(() => {
          this.addButton.textContent = originalText;
          this.addButton.disabled = false;
        }, 1800);
      }
    } catch (error) {
      this.addButton.textContent = error.message || 'Bitte erneut versuchen';
      this.addButton.disabled = false;
    }
  }
}

const initializeBundleBuilders = (scope = document) => {
  scope.querySelectorAll('[data-rc-bundle]:not([data-initialized])').forEach((root) => {
    root.dataset.initialized = 'true';
    new RealityCheckBundleBuilder(root);
  });
};

initializeBundleBuilders();
document.addEventListener('shopify:section:load', (event) => initializeBundleBuilders(event.target));
})();
